/**
 * ConcernsCard — the RAID log (risks / assumptions / issues / decisions).
 *
 * Rendered inside the RAID Log drawer on both the project page and the
 * initiative page. Every entry is fully editable in place (Brett,
 * 2026-09-15): the pencil opens the same form used to add one, so type,
 * title, details, severity, owner, and status can all be corrected
 * after the fact. Resolve / reopen stay as one-click shortcuts for the
 * common status flip.
 *
 * Resolved rows are kept in the list, dimmed with a green check —
 * Brett's call: a decided decision is project history, users want to
 * refer back to it rather than have it disappear.
 */

import { useMemo, useState } from "react";
import { AlertTriangle, Plus, X, Save, Loader2, ShieldAlert, HelpCircle, Flame, GitBranch, Check, RotateCcw, Pencil, FileSpreadsheet } from "lucide-react";
import { useCan } from "@/lib/roles";
import { shortDate } from "@/lib/format";
import { RaidImportPanel } from "@/components/project/RaidImportPanel";
import { cn } from "@/lib/cn";
import { toast } from "sonner";
import { RichText } from "@/components/ui/RichText";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { useRoster } from "@/hooks/useRoster";
import {
  useConcerns,
  useAddConcern,
  useUpdateConcern,
  useDeleteConcern,
  type Concern,
  type ConcernType,
  type ConcernParentType,
  type Severity,
} from "@/hooks/useConcerns";

const TYPE_META: Record<ConcernType, { label: string; plural: string; icon: typeof Flame; tint: string }> = {
  risk:       { label: "Risk",       plural: "Risks",       icon: Flame,        tint: "text-rose-500" },
  issue:      { label: "Issue",      plural: "Issues",      icon: ShieldAlert,  tint: "text-amber-500" },
  decision:   { label: "Decision",   plural: "Decisions",   icon: GitBranch,    tint: "text-brand-navy-500" },
  assumption: { label: "Assumption", plural: "Assumptions", icon: HelpCircle,   tint: "text-sky-500" },
};

const SEVERITY_DOT: Record<Severity, string> = {
  high:   "bg-rose-500",
  medium: "bg-amber-500",
  low:    "bg-slate-400",
};

// Status values that count as "resolved" — used for visual treatment
// (green check, dimmed row), not for hiding the row from the list.
// Mirrors the connector's _CONCERN_TERMINAL.
const TERMINAL_STATUSES = new Set([
  "closed", "resolved", "decided", "confirmed", "invalidated", "reversed",
]);

// The status vocabulary per type, in lifecycle order. Same words the
// connector recognizes (its ordering clause treats open / mitigating /
// in_progress / proposed as active). Editing a status was previously
// impossible, so mitigating / in_progress were unreachable.
const STATUS_OPTIONS: Record<ConcernType, string[]> = {
  risk:       ["open", "mitigating", "closed"],
  issue:      ["open", "in_progress", "resolved"],
  decision:   ["proposed", "decided", "reversed"],
  assumption: ["open", "confirmed", "invalidated"],
};

// Default open status used when "reopening" a resolved item — the
// first (most-open) status in that type's vocabulary.
const REOPEN_TARGET: Record<ConcernType, string> = {
  risk: "open",
  issue: "open",
  decision: "proposed",
  assumption: "open",
};

// One-click "resolve" target per type.
const RESOLVE_TARGET: Record<ConcernType, string> = {
  risk: "closed",
  issue: "resolved",
  decision: "decided",
  assumption: "confirmed",
};

type Filter = "all" | ConcernType;

export function ConcernsCard({
  projectId,
  parentType = "project",
}: {
  projectId: string;
  /** Default "project" — existing ProjectDetail call site keeps working
   *  without changes. */
  parentType?: ConcernParentType;
}) {
  const [filter, setFilter] = useState<Filter>("all");
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const { isPmo } = useCan();
  const concernsQ = useConcerns(projectId, undefined, parentType);

  const all = concernsQ.data ?? [];

  // Sort: open items first (by severity desc, then most recent), then
  // resolved items (most-recently resolved first). Resolved rows stay
  // in the list — they're project history, not noise.
  const sorted = useMemo(() => {
    const SEVERITY_RANK: Record<string, number> = { high: 0, medium: 1, low: 2 };
    const isOpen = (c: Concern) => !TERMINAL_STATUSES.has(c.status);
    return [...all].sort((a, b) => {
      const ao = isOpen(a);
      const bo = isOpen(b);
      if (ao !== bo) return ao ? -1 : 1;
      if (ao) {
        const sa = SEVERITY_RANK[a.severity ?? ""] ?? 3;
        const sb = SEVERITY_RANK[b.severity ?? ""] ?? 3;
        if (sa !== sb) return sa - sb;
      }
      const ka = (a as any).updated_at ?? (a as any).created_at ?? "";
      const kb = (b as any).updated_at ?? (b as any).created_at ?? "";
      return String(kb).localeCompare(String(ka));
    });
  }, [all]);

  // Counts on the filter chips show OPEN items only — that's what
  // demands attention. Resolved items stay in the list but don't
  // count toward the badge.
  const counts = useMemo(() => {
    const c: Record<Filter, number> = { all: 0, risk: 0, issue: 0, decision: 0, assumption: 0 };
    for (const item of all) {
      if (TERMINAL_STATUSES.has(item.status)) continue;
      c.all += 1;
      c[item.type] += 1;
    }
    return c;
  }, [all]);

  // Every row renders. The old 5-row cap dated from when this card sat
  // inline on the Overview tab and promised a "drawer coming soon" —
  // the drawer is what it lives in now, so capping hid entries the
  // user came here to read and edit (Brett, 2026-09-15).
  const visible = filter === "all" ? sorted : sorted.filter((c) => c.type === filter);

  return (
    <div className="rounded-xl border border-line bg-surface p-6">
      <div className="mb-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <h2 className="text-sm font-semibold text-ink-2">RAID Log</h2>
          <span className="text-[10px] uppercase tracking-wider text-ink-subtle">risks · issues · decisions · assumptions</span>
        </div>
        {!adding && !importing && (
          <div className="flex items-center gap-1.5">
            {/* Spreadsheet import (2026-10-02): projects only, PMO only. */}
            {parentType === "project" && isPmo && (
              <button
                onClick={() => setImporting(true)}
                className="flex items-center gap-1 rounded-lg border border-line bg-surface px-3 py-1.5 text-[11px] font-medium text-ink-3 hover:bg-raised transition-colors"
                title="Load RAID entries from an .xlsx or .csv file"
                data-testid="raid-import-open"
              >
                <FileSpreadsheet className="h-3 w-3" /> Import from file
              </button>
            )}
            <button
              onClick={() => setAdding(true)}
              className="flex items-center gap-1 rounded-lg border border-line bg-surface px-3 py-1.5 text-[11px] font-medium text-ink-3 hover:bg-raised transition-colors"
            >
              <Plus className="h-3 w-3" /> Add
            </button>
          </div>
        )}
      </div>

      {importing && <div className="mb-3"><RaidImportPanel projectId={projectId} onClose={() => setImporting(false)} /></div>}

      {/* Type tabs with open counts. Hidden when adding to keep focus on the form. */}
      {!adding && !importing && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label="All" count={counts.all} />
          {(Object.keys(TYPE_META) as ConcernType[]).map((t) => (
            <FilterChip
              key={t}
              active={filter === t}
              onClick={() => setFilter(t)}
              label={TYPE_META[t].plural}
              count={counts[t]}
            />
          ))}
        </div>
      )}

      {adding && (
        <ConcernForm
          projectId={projectId}
          parentType={parentType}
          initialType={filter === "all" ? "risk" : filter}
          onClose={() => setAdding(false)}
        />
      )}

      {!adding && (
        <>
          {concernsQ.isLoading ? (
            <p className="py-6 text-center text-xs text-ink-subtle">Loading…</p>
          ) : visible.length === 0 ? (
            <p className="py-6 text-center text-sm italic text-ink-subtle">
              {filter === "all"
                ? "No entries yet. Add a risk, issue, decision, or assumption as it surfaces."
                : `No ${TYPE_META[filter as ConcernType].plural.toLowerCase()} yet.`}
            </p>
          ) : (
            <ul className="divide-y divide-line-soft">
              {visible.map((c) => (
                <ConcernRow key={c.id} projectId={projectId} parentType={parentType} concern={c} />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function FilterChip({
  active, onClick, label, count,
}: { active: boolean; onClick: () => void; label: string; count: number }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors",
        active
          ? "border-brand-navy-300 dark:border-brand-navy-500/40 bg-brand-navy-50 dark:bg-brand-navy-500/10 text-brand-navy-700 dark:text-brand-navy-300"
          : "border-line bg-surface text-ink-3 hover:bg-raised",
      )}
    >
      {label}
      {count > 0 && (
        <span className={cn("ml-1.5 text-[10px]", active ? "text-brand-navy-500" : "text-ink-subtle")}>
          {count}
        </span>
      )}
    </button>
  );
}

function ConcernRow({ projectId, parentType, concern }: { projectId: string; parentType: ConcernParentType; concern: Concern }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const update = useUpdateConcern(projectId, parentType);
  const del = useDeleteConcern(projectId, parentType);
  const meta = TYPE_META[concern.type];
  const Icon = meta.icon;
  const isResolved = TERMINAL_STATUSES.has(concern.status);

  const handleResolve = async () => {
    try {
      await update.mutateAsync({ id: concern.id, patch: { status: RESOLVE_TARGET[concern.type] } });
      toast.success(`${meta.label} marked ${RESOLVE_TARGET[concern.type]}.`);
    } catch {
      toast.error("Failed to update.");
    }
  };

  const handleReopen = async () => {
    try {
      await update.mutateAsync({ id: concern.id, patch: { status: REOPEN_TARGET[concern.type] } });
      toast.success(`${meta.label} reopened.`);
    } catch {
      toast.error("Failed to reopen.");
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Delete this ${meta.label.toLowerCase()}?`)) return;
    try {
      await del.mutateAsync(concern.id);
    } catch {
      toast.error("Failed to delete.");
    }
  };

  // Editing replaces the row with the same form used to add an entry,
  // pre-filled — one form, so add and edit can't drift apart.
  if (editing) {
    return (
      <li className="py-3">
        <ConcernForm
          projectId={projectId}
          parentType={parentType}
          initialType={concern.type}
          existing={concern}
          onClose={() => setEditing(false)}
        />
      </li>
    );
  }

  return (
    <li className="py-3">
      <div className={cn("flex items-start gap-3", isResolved && "opacity-65")}>
        {/* Expand/collapse the details body. Actions sit OUTSIDE this
            button so the row has no nested interactive elements. */}
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex flex-1 items-start gap-3 text-left"
          title={concern.body ? "Show details" : undefined}
        >
          {/* Lead glyph — green check for resolved items, severity dot
              otherwise. */}
          {isResolved ? (
            <span
              className="mt-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
              title={concern.status}
            >
              <Check className="h-3 w-3" />
            </span>
          ) : (
            <span
              className={cn(
                "mt-1.5 inline-block h-2 w-2 flex-shrink-0 rounded-full",
                concern.severity ? SEVERITY_DOT[concern.severity] : "bg-muted",
              )}
              title={concern.severity ?? "no severity"}
            />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Icon className={cn("h-3.5 w-3.5", meta.tint)} />
              <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
                {meta.label}
              </span>
              <span
                className={cn(
                  "rounded-full px-2 py-0.5 text-[10px] font-medium",
                  isResolved
                    ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                    : "bg-raised text-ink-3",
                )}
              >
                {concern.status}
              </span>
              {concern.owner && (
                <span className="text-[10px] text-ink-subtle">· {concern.owner}</span>
              )}
              {/* Imported from a spreadsheet: the file's ID, category and date raised ride along. */}
              {concern.external_ref && (
                <span className="rounded bg-raised px-1.5 py-0.5 font-mono text-[10px] text-ink-muted" title="ID in the imported RAID log" data-testid="concern-ref">{concern.external_ref}</span>
              )}
              {concern.category && <span className="text-[10px] text-ink-subtle">· {concern.category}</span>}
              {concern.raised_on && <span className="text-[10px] text-ink-subtle" title="Date raised">· {shortDate(concern.raised_on)}</span>}
            </div>
            <p
              className={cn(
                "mt-0.5 text-sm",
                expanded ? "" : "truncate",
                isResolved ? "text-ink-3 line-through decoration-ink-faint" : "text-ink",
              )}
            >
              {concern.title}
            </p>
            {expanded && concern.body && (
              <div className="mt-2">
                <RichText text={concern.body} className="text-xs" />
              </div>
            )}
          </div>
        </button>
        <div className="flex flex-shrink-0 gap-1">
          <button
            onClick={() => setEditing(true)}
            className="rounded-md border border-line bg-surface p-1 text-ink-subtle transition-colors hover:bg-brand-navy-50 dark:hover:bg-brand-navy-500/15 hover:text-brand-navy-600 dark:hover:text-brand-navy-400"
            title={`Edit this ${meta.label.toLowerCase()}`}
            aria-label={`Edit ${concern.title}`}
          >
            <Pencil className="h-3 w-3" />
          </button>
          {isResolved ? (
            <button
              onClick={handleReopen}
              className="rounded-md border border-line bg-surface p-1 text-ink-subtle transition-colors hover:bg-amber-50 dark:hover:bg-amber-500/15 hover:text-amber-600 dark:hover:text-amber-400"
              title="Reopen"
              aria-label={`Reopen ${concern.title}`}
            >
              {update.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
            </button>
          ) : (
            <button
              onClick={handleResolve}
              className="rounded-md border border-line bg-surface p-1 text-ink-muted transition-colors hover:bg-emerald-50 dark:hover:bg-emerald-500/15 hover:text-emerald-600 dark:hover:text-emerald-400"
              title={`Mark ${RESOLVE_TARGET[concern.type]}`}
              aria-label={`Mark ${concern.title} ${RESOLVE_TARGET[concern.type]}`}
            >
              {update.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
            </button>
          )}
          <button
            onClick={handleDelete}
            className="rounded-md border border-line bg-surface p-1 text-ink-subtle transition-colors hover:bg-rose-50 dark:hover:bg-rose-500/15 hover:text-rose-600 dark:hover:text-rose-400"
            title="Delete"
            aria-label={`Delete ${concern.title}`}
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      </div>
    </li>
  );
}

/**
 * One form for both adding and editing an entry. `existing` switches it
 * into edit mode: fields pre-fill, the save PATCHes instead of POSTing,
 * and the status picker appears (status on a new entry is the type's
 * default, applied server-side).
 */
function ConcernForm({
  projectId, parentType, initialType, existing, onClose,
}: {
  projectId: string;
  parentType: ConcernParentType;
  initialType: ConcernType;
  existing?: Concern;
  onClose: () => void;
}) {
  const [type, setType] = useState<ConcernType>(existing?.type ?? initialType);
  const [title, setTitle] = useState(existing?.title ?? "");
  const [severity, setSeverity] = useState<Severity | "">(existing?.severity ?? "");
  const [body, setBody] = useState(existing?.body ?? "");
  const [owner, setOwner] = useState(existing?.owner ?? "");
  const [status, setStatus] = useState(existing?.status ?? "");
  const add = useAddConcern(projectId, parentType);
  const update = useUpdateConcern(projectId, parentType);
  const rosterQ = useRoster();
  const roster = Array.isArray(rosterQ.data) ? rosterQ.data : [];

  const isEdit = !!existing;
  const showSeverity = type === "risk" || type === "issue";
  const pending = add.isPending || update.isPending;

  // Options for this type, plus the current value when it predates the
  // vocabulary — never silently coerce someone's existing status.
  const statusChoices = useMemo(() => {
    const base = STATUS_OPTIONS[type];
    return status && !base.includes(status) ? [status, ...base] : base;
  }, [type, status]);

  // Changing type in edit mode can orphan the status (a risk's "closed"
  // means nothing on a decision) — move it to the new type's matching
  // slot, keeping resolved-ness.
  const handleTypeChange = (next: ConcernType) => {
    if (isEdit && status) {
      const wasTerminal = TERMINAL_STATUSES.has(status);
      setStatus(wasTerminal ? RESOLVE_TARGET[next] : REOPEN_TARGET[next]);
    }
    setType(next);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Title is required.");
      return;
    }
    try {
      if (isEdit) {
        await update.mutateAsync({
          id: existing!.id,
          patch: {
            type,
            title: title.trim(),
            body: body.trim() || null,
            severity: showSeverity ? (severity || null) : null,
            owner: owner.trim() || null,
            status: status || undefined,
          },
        });
        toast.success(`${TYPE_META[type].label} updated.`);
      } else {
        await add.mutateAsync({
          type,
          title: title.trim(),
          body: body.trim() || null,
          severity: showSeverity ? (severity || null) : null,
          owner: owner.trim() || null,
        });
        toast.success(`${TYPE_META[type].label} added.`);
      }
      onClose();
    } catch {
      toast.error(isEdit ? "Failed to save." : "Failed to add.");
    }
  };

  return (
    <div className="space-y-3 rounded-lg border border-brand-navy-100 dark:border-brand-navy-500/30 bg-brand-navy-50/30 dark:bg-brand-navy-500/10 p-4">
      <div className="flex flex-wrap items-center gap-2">
        {(Object.keys(TYPE_META) as ConcernType[]).map((t) => (
          <button
            key={t}
            onClick={() => handleTypeChange(t)}
            className={cn(
              "rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors",
              type === t
                ? "border-brand-navy-400 bg-surface text-brand-navy-700 dark:text-brand-navy-300"
                : "border-line bg-surface text-ink-muted hover:bg-raised",
            )}
          >
            {TYPE_META[t].label}
          </button>
        ))}
      </div>

      <input
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={`Short headline for this ${TYPE_META[type].label.toLowerCase()}`}
        aria-label="Entry title"
        className="w-full rounded-lg border border-line-strong bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-subtle focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-400"
      />

      <RichTextEditor
        value={body}
        onChange={setBody}
        rows={4}
        placeholder="Optional details — context, impact, mitigation."
      />

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={owner}
          onChange={(e) => setOwner(e.target.value)}
          list="raid-owner-names"
          placeholder="Owner"
          aria-label="Entry owner"
          className="w-40 rounded-lg border border-line-strong bg-surface px-2 py-1.5 text-xs text-ink placeholder:text-ink-subtle focus:border-brand-navy-400 focus:outline-none"
        />
        <datalist id="raid-owner-names">
          {roster.map((m) => (
            <option key={m.name} value={m.name} />
          ))}
        </datalist>
        {isEdit && (
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            aria-label="Entry status"
            className="rounded-lg border border-line-strong bg-surface px-2 py-1.5 text-xs text-ink focus:border-brand-navy-400 focus:outline-none"
          >
            {statusChoices.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        )}
      </div>

      {showSeverity && (
        <div className="flex items-center gap-2">
          <span className="text-[11px] uppercase tracking-wider text-ink-muted">Severity</span>
          {(["low", "medium", "high"] as Severity[]).map((s) => (
            <button
              key={s}
              onClick={() => setSeverity(severity === s ? "" : s)}
              className={cn(
                "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium capitalize transition-colors",
                severity === s
                  ? "border-line-strong bg-surface text-ink"
                  : "border-line bg-surface text-ink-muted hover:bg-raised",
              )}
            >
              <span className={cn("h-2 w-2 rounded-full", SEVERITY_DOT[s])} />
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="flex justify-end gap-1.5">
        <button
          onClick={onClose}
          className="flex items-center gap-1 rounded-lg border border-line bg-surface px-3 py-1.5 text-[11px] font-medium text-ink-3 hover:bg-raised transition-colors"
        >
          <X className="h-3 w-3" /> Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={pending}
          className="flex items-center gap-1 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-[11px] font-medium text-white hover:bg-brand-navy-700 transition-colors disabled:opacity-50"
        >
          {pending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
          {pending ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
